import { Canvas } from "@react-three/fiber";
import { Scene } from "./Scene";
import { MATERIAL_PRESETS } from "../utils";
import type { MaterialPreset } from "../types";
import { MantineProvider, Stack, Tabs } from "@mantine/core";
import { Html } from "@react-three/drei";
import { useState, type ReactElement } from "react";

interface Props {
  stls: Array<{ url: string; name: string }>;
  materialPreset?: MaterialPreset;
  style?: React.CSSProperties;
  bottomOverlay?: ReactElement;
  topOverlay?: ReactElement;
  interactionsEnabled?: boolean;
}

export const ModelVisualizer = ({
  stls,
  materialPreset,
  style,
  bottomOverlay,
  topOverlay,
  interactionsEnabled = true,
}: Props) => {
  const [activeStlUrl, setActiveStlUrl] = useState<string | null>(
    stls[0]?.url ?? null
  );

  return (
    <Stack h="100%" gap={0}>
      {stls.length > 1 && (
        <Tabs color="teal" value={activeStlUrl} onChange={setActiveStlUrl}>
          <Tabs.List>
            {stls.map((stl, index) => (
              <Tabs.Tab value={stl.url} key={index}>
                {stl.name}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      )}
      {activeStlUrl && (
        <Canvas
          style={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            pointerEvents: interactionsEnabled ? "auto" : "none",
            ...style,
          }}
        >
          <Scene
            url={activeStlUrl}
            material={MATERIAL_PRESETS[materialPreset ?? "ZIRCONIA_MULTILAYER"]}
          />

          {(bottomOverlay || topOverlay) && (
            <Html fullscreen zIndexRange={[2, 0]}>
              <MantineProvider>
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    padding: 12,
                    zIndex: 2,
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      pointerEvents: interactionsEnabled ? "auto" : "none",
                    }}
                  >
                    {topOverlay}
                  </div>
                </div>

                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    padding: 12,
                    zIndex: 1,
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      pointerEvents: interactionsEnabled ? "auto" : "none",
                    }}
                  >
                    {bottomOverlay}
                  </div>
                </div>
              </MantineProvider>
            </Html>
          )}
        </Canvas>
      )}
    </Stack>
  );
};
